/** Por que a requisição nasceu sem atendimento na Oficina (at-12). */
export type ConfirmacaoVinculo = 'SEM_ATENDIMENTO' | 'EVENTO_DIVERGENTE';

/** Onde o veículo está e que veículo é — decidem o destino da requisição no ERP (at-14). */
export type TipoOficina = 'INTERNA' | 'EXTERNA';
export type TipoVeiculo = 'CARRO' | 'MOTO';

export interface OrcamentoCilia {
  id: string;
  /** at-17: requisição de teste — o Motivo no Nasajon começa por "TESTE - NÃO COMPRAR". */
  teste?: boolean;
  /** Perda total guardada na requisição (at-15) — é o que a lista usa para o selo. */
  ptSituacao?: 'PT_NAS_DUAS' | 'PT_SO_NA_MAIOR' | 'ABAIXO' | 'SEM_FIPE' | 'SEM_PRECO' | null;
  /**
   * Pré-seleção do "Concluir e Enviar", calculada pelo SERVIDOR a partir do XML (at-14). `null` =
   * o XML não decide, e a escolha fica com quem conclui. Ausente no backend anterior à at-14.
   */
  tipoOficinaSugerido?: TipoOficina | null;
  tipoVeiculoSugerido?: TipoVeiculo | null;
  // Nulo desde a at-12: a requisição pode começar sem o veículo na Oficina, e é ligada depois.
  carId: string | null;
  vinculoOrigem?: 'IMPORTACAO' | 'AUTOMATICO_EVENTO' | 'AUTOMATICO_PLACA' | null;
  vinculadoEm?: string | null;
  semVinculoMotivo?: ConfirmacaoVinculo | null;
  placa: string;
  numeroCilia: string;
  versaoCilia: number;
  nomeVersaoCilia: string;
  numeroSinistro: string;
  numeroEvento: string;
  statusCilia: string;
  tipo: 'INICIAL' | 'COMPLEMENTAR';
  orcamentoPaiId?: string | null;
  estado:
    | 'EM_CONFERENCIA'
    | 'ENVIADO'
    | 'AGUARDANDO_APROVACAO'
    | 'APROVADO'
    | 'REJEITADO'
    | 'EM_AJUSTE'
    | 'AGUARDANDO_AUTORIZACAO'
    | 'AUTORIZACAO_NEGADA';
  // Complementos (29/07/2026): resumo do diff calculado no import + decisão do
  // Chefe quando o orçamento passou pelo limite de complementos.
  diffResumo?: DiffResumo | null;
  autorizadoPorId?: string | null;
  autorizadoEm?: string | null;
  autorizacaoNota?: string | null;
  dadosVeiculo: {
    placa?: string;
    chassi?: string;
    cor?: string;
    tipoPintura?: string;
    nomeVeiculo?: string;
    modelo?: string;
    marca?: string;
    tipo?: string;
  };
  dadosOficina: {
    nome?: string;
    cnpj?: string;
    telefone?: string;
    email?: string;
    endereco?: string;
  };
  totais: Record<string, string> | null;
  importadoPorId: string;
  conferidoPorId: string | null;
  conferidoEm: string | null;
  createdAt: string;
  updatedAt: string;
  itens?: OrcamentoItem[];
  requisicoes?: Requisicao[];
  /**
   * `requisicoes` (13/08/2026) distingue os dois desfechos do estado `ENVIADO`: conferência
   * que gerou requisição × conferência que não gerou (versão sem peça comprável).
   * **Opcional** porque resposta em cache — ou de um backend anterior — não traz o campo;
   * nesse caso o consumidor assume o caso comum (houve requisição).
   */
  _count?: { itens: number; requisicoes?: number };
  // Listagem: posição do complemento na cadeia (1 = 1º complemento); null =
  // inicial ou complemento negado (fora da conta)
  complementoOrdinal?: number | null;
  // Nomes desnormalizados do JWT no ato (29/07/2026) — null = registro antigo
  importadoPorNome?: string | null;
  conferidoPorNome?: string | null;
  autorizadoPorNome?: string | null;
  // Versões do mesmo orçamento já importadas (detalhe) — base da coluna
  // "Origem" e do painel "Histórico do Orçamento"
  cadeia?: CadeiaVersao[];
  /**
   * Detalhe (at-18): prévia do "Motivo da Requisição", montada pelo servidor com a MESMA função do
   * envio. `vip` só quando há peça CAMINHO VIP. Ausente no backend anterior à at-18.
   */
  motivoPrevisto?: { normal: string; vip: string | null };
}

export interface CadeiaVersao {
  id: string;
  versaoCilia: number;
  tipo: 'INICIAL' | 'COMPLEMENTAR';
  estado: string;
  createdAt: string;
  importadoPorNome?: string | null;
  conferidoEm?: string | null;
  conferidoPorNome?: string | null;
  diffResumo?: DiffResumo | null;
  autorizadoEm?: string | null;
  autorizadoPorNome?: string | null;
  autorizacaoNota?: string | null;
  _count?: { itens: number };
  requisicoes?: Array<{
    versao: number;
    status: RequisicaoStatus;
    vip?: boolean;
    nasajonCodigo?: string | null;
    motivoRejeicao?: string | null;
    createdAt: string;
  }>;
}

// Versão no histórico da cadeia (modal "Histórico do Orçamento") — inclui as
// peças de cada versão, diferente da cadeia leve usada na coluna "Origem".
export interface HistoricoVersao {
  id: string;
  versaoCilia: number;
  tipo: 'INICIAL' | 'COMPLEMENTAR';
  estado: string;
  createdAt: string;
  importadoPorNome?: string | null;
  conferidoEm?: string | null;
  conferidoPorNome?: string | null;
  diffResumo?: DiffResumo | null;
  autorizadoEm?: string | null;
  autorizadoPorNome?: string | null;
  autorizacaoNota?: string | null;
  itens: Array<{
    codigoCilia: string;
    descricaoCilia: string;
    quantidade: string;
    preco: string;
    situacaoCompra: string;
    origemDiff?: 'NOVO' | 'HERDADO' | 'QTD_AUMENTADA';
    quantidadeJaSolicitada?: string | null;
    versaoOrigem?: number | null;
    esteiraNome?: string | null;
  }>;
  requisicoes?: Array<{
    versao: number;
    status: RequisicaoStatus;
    vip?: boolean;
    nasajonCodigo?: string | null;
    motivoRejeicao?: string | null;
  }>;
}

export interface OrcamentoItem {
  id: string;
  ordem: number;
  codigoCilia: string;
  descricaoCilia: string;
  tipoItem: 'PECA' | 'SERVICO';
  tipoCobertura: string | null;
  troca: boolean;
  fornecimento: string;
  tipoRemocaoInstalacao: string | null;
  comentario: string | null;
  inclusaoManual: boolean;
  quantidade: string;
  preco: string;
  precoLiquido: string;
  percentualDesconto: string;
  pmcReferencia: string | null;
  pmcConsultadoEm: string | null;
  mediaCompras: string | null; // média ponderada das compras na janela (referência preferida)
  mediaComprasQtd: number;
  mediaComprasEm: string | null;
  esteiraIdSoac: string | null;
  esteiraNome: string | null;
  // `EM_ESTOQUE` saiu em 20/08/2026 (ADR 0006): o estoque do Nasajon não governa a compra, e
  // com a situação foram embora `compraForcada`, `justificativaForcarCompra` e
  // `quantidadeAbatidaEstoque`. O saldo continua no banco, mas nenhuma tela o lê.
  situacaoCompra: 'FORA_DO_FLUXO' | 'AGUARDANDO_COBERTURA' | 'A_COMPRAR' | 'ENVIADO' | 'RECEBIDO';
  produtoNasajonId: string | null;
  codigoNasajon: string | null;
  produtoStatus: 'NAO_VERIFICADO' | 'EXISTENTE' | 'A_CRIAR' | 'CRIADO';
  ncm: string | null; // legado — o campo saiu da conferência (28/07/2026)
  /**
   * Unidade de medida, em **código** do cadastro do ERP (`UN`, `KIT`, `M2`…).
   *
   * Editável na conferência desde a at-11, mas **só em peça que vai ser criada**: produto que já
   * existe usa a `unidade_padrao` do próprio cadastro (medido: 12/12 itens reais), e o backend
   * recusa a troca nesse caso — a requisição não pode contradizer o produto que ela referencia.
   */
  unidade: string | null;
  origemMercadoria: string | null;
  grupoInventario: string | null;
  recebidoEm: string | null;
  /**
   * 🔴 **CAMINHO VIP**: a peça já foi comprada na rua, e o comprador **não** deve comprá-la.
   *
   * Vai para uma requisição separada, cujo "Motivo da Requisição" começa com o marcador que diz
   * isso — é convenção de texto livre, sem campo próprio no ERP, e é por isso que quem a escreve
   * é o sistema. Marcado por peça; a conclusão resolve a diferença criando duas requisições.
   */
  vip?: boolean;
  // Diff do complemento (29/07/2026): HERDADO = bloqueado ("Já pedida no orç. #X");
  // QTD_AUMENTADA = comprável pela diferença (quantidadeJaSolicitada registra o resto).
  origemDiff?: 'NOVO' | 'HERDADO' | 'QTD_AUMENTADA';
  quantidadeJaSolicitada?: string | null;
  // Versão do Cilia em que a peça apareceu pela 1ª vez (coluna "Origem";
  // null = item importado antes da coluna existir)
  versaoOrigem?: number | null;
}

export interface DiffResumo {
  contadores: {
    novos: number;
    herdados: number;
    qtdAumentada: number;
    reprocessados: number;
    removidos: number;
    qtdReduzida: number;
  };
  /**
   * `codigoNasajon` (13/08/2026): o código que identifica a peça FORA do Cilia — é por ele
   * que a tela cruza uma peça removida/reduzida com os itens de uma cotação aberta no Portal
   * (a solicitação leva `codigoNasajon ?? codigoCilia` como `codigoProduto`).
   * **Opcional**: diff gravado antes desta data não tem o campo, e o mesmo fallback resolve.
   */
  removidos: Array<{
    codigoCilia: string;
    codigoNasajon?: string | null;
    descricao: string;
    quantidade: number;
    situacaoCompra: string;
    origemVersao: number;
    origemNumero: string;
  }>;
  quantidadeReduzida: Array<{
    codigoCilia: string;
    codigoNasajon?: string | null;
    descricao: string;
    qtdAnterior: number;
    qtdNova: number;
    situacaoCompra: string;
    origemVersao: number;
  }>;
  reprocessados: Array<{ codigoCilia: string; descricao: string; situacaoAnterior: string; origemVersao: number }>;
}

export interface NotificacaoModulo {
  id: string;
  tipo: string;
  titulo: string;
  mensagem: string;
  payload?: { orcamentoId?: string; numeroCilia?: string; versao?: number; placa?: string } | null;
  lida: boolean;
  createdAt: string;
}

/**
 * Situação da requisição, espelhando o `estado_workflow` do ERP (at-11, ADR 0007).
 *
 * 🔴 O eixo da aprovação é o **`estado_workflow`**, nunca a `situacao` — medido em 18/09/2026,
 * sobre 300 requisições reais: uma requisição APROVADA continua `situacao = pendente` até virar
 * pedido. `CANCELADA` vale como reprovação para o orçamento, com rótulo próprio na tela: quem lê
 * não pode concluir que um gestor recusou.
 */
export type RequisicaoStatus = 'CRIADA' | 'EM_APROVACAO' | 'APROVADA' | 'REPROVADA' | 'CANCELADA';

/**
 * Requisição de compra da versão, criada no Nasajon por API (at-11, ADR 0007).
 *
 * ⚠️ Substituiu a `Solicitacao` (o CSV que o comprador lançava à mão). Sumiram com ela o `modo`
 * (há um caminho só) e o `nasajonRef` (que nunca foi preenchido, porque não havia rota).
 */
export interface Requisicao {
  versao: number;
  status: RequisicaoStatus;
  motivoRejeicao: string | null;

  /**
   * Id e código no ERP. O **código** é o que a tela do Nasajon mostra, e é por ele que o comprador
   * acha o documento.
   *
   * ⚠️ Nulos quando `simulada`: em `NASAJON_WRITE_MODE=simulacao` nada foi criado lá, e exibir um
   * número faria a tela prometer uma requisição que não existe.
   */
  nasajonId?: string | null;
  nasajonCodigo?: string | null;
  simulada: boolean;

  /**
   * 🔴 A requisição que carrega o marcador **CAMINHO VIP** (peças já compradas na rua, que o
   * comprador **não** deve comprar). Numa versão mista existem duas requisições da mesma versão.
   */
  vip: boolean;

  /** Destino no ERP (at-14). Nulos nas requisições anteriores, que nasceram sem local de uso. */
  tipoOficina?: TipoOficina | null;
  tipoVeiculo?: TipoVeiculo | null;
  localUsoCodigo?: string | null;
  localEstoqueCodigo?: string | null;
  /** 🔴 O Nasajon mostrou OUTRA requisição com o mesmo número logo depois da criação (at-14). */
  codigoRepetido?: boolean;

  /**
   * O "Motivo da Requisição" exatamente como foi enviado ao Nasajon (at-18). Nulo nas requisições
   * anteriores à gravação: a tela diz "não registrado", nunca recalcula.
   */
  motivoRequisicao?: string | null;

  /** O que o ERP respondeu na última conferência, cru — para a tela mostrar o vocabulário dele. */
  estadoWorkflowErp?: string | null;
  situacaoErp?: string | null;
  /** Quando a situação foi conferida pela última vez. Sem isso, "criada" não se distingue de
   * "ninguém perguntou desde então". */
  ultimaVerificacaoEm?: string | null;

  /** Legado: o CSV do modo assistido, que deixou de ser gerado na at-11. */
  arquivoStorageKey?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Auditoria do módulo (at-05, 16/08/2026) ────────────────────────────────────

/** Uma CADEIA de orçamento (número do Cilia com todas as suas versões). */
export interface AuditoriaOrcamentoItem {
  id: string; // id da versão mais recente — é por ele que a timeline resolve a cadeia
  numeroCilia: string;
  placa: string | null;
  numeroEvento: string | null;
  veiculo: string | null;
  qtdVersoes: number;
  estadoAtual: string | null;
  createdAt: string | null;
}

/**
 * O que um evento clicável abre.
 *
 * ⚠️ O payload é `null` em registro **anterior a esta entrega** (não havia snapshot): o
 * modal mostra o aviso de detalhamento indisponível em vez de sumir com o evento — mesmo
 * comportamento do `PlanningComparisonModal` do SOAC para logs legados.
 */
export type AberturaEventoCilia =
  | { tipo: 'ITENS_IMPORTADOS'; itens: Record<string, any>[] | null }
  | { tipo: 'DIFF_COMPLEMENTO'; diff: Record<string, any> | null; itens: Record<string, any>[] | null }
  | { tipo: 'ITENS_ENVIADOS'; itens: Record<string, any>[] | null; titulo: string }
  | { tipo: 'REVERIFICACAO'; itens: Record<string, any>[] | null }
  | { tipo: 'ITENS_DEVOLVIDOS'; itens: Record<string, any>[] | null };

export interface AuditoriaEventoCilia {
  id: string;
  em: string;
  /** `SISTEMA` = ação de cron, sem pessoa por trás (o backend grava autor nulo). */
  origem: 'INTERNO' | 'SISTEMA';
  tipo: string;
  titulo: string;
  autor: string | null;
  /** Id de quem agiu, só quando o nome NÃO pôde ser resolvido (nunca junto com `autor`). */
  autorId?: string | null;
  detalhes: string[];
  abrir: AberturaEventoCilia | null;
}

export interface AuditoriaTimelineCilia {
  orcamento: {
    id: string;
    numeroCilia: string;
    placa: string | null;
    numeroEvento: string | null;
    veiculo: string | null;
    estadoAtual: string | null;
    qtdVersoes: number;
    importadoPorNome: string | null;
    createdAt: string | null;
  };
  eventos: AuditoriaEventoCilia[];
  autores: string[];
  tiposPresentes: string[];
}

export interface AuditoriaItemCilia {
  item: { id: string; codigo: string; descricao: string };
  eventos: AuditoriaEventoCilia[];
}

export interface AuditoriaConfiguracaoItem {
  id: string;
  em: string;
  autor: string | null;
  autorId?: string | null;
  acao: string;
  alvo: string | null;
  detalhes: Array<{ rotulo: string; de: string; para: string }>;
}

/**
 * Rótulo de quem agiu num evento de auditoria (16/08/2026).
 *
 * Três casos, e nenhum deles imprime UUID: **Sistema** (cron, sem `autorId`), o **nome**
 * quando o backend o resolveu, e **"Usuário não identificado"** quando nem o log nem o mapa
 * de nomes do módulo conheciam aquele id — aí o id fica só no `title`, alcançável para quem
 * for investigar, fora do caminho de quem só está lendo.
 */
export function rotuloDoAutor(evento: { autor: string | null; autorId?: string | null }): {
  texto: string;
  title?: string;
} {
  if (evento.autor) return { texto: evento.autor };
  if (evento.autorId) return { texto: 'Usuário não identificado', title: `Identificador: ${evento.autorId}` };
  return { texto: 'Sistema' };
}
